import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { DollarSign, Loader2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const REVENUE_SOURCES = ['Flight Commission', 'Hotel Commission', 'Tour Package', 'Transfer Commission', 'Attraction Tickets', 'Advertising', 'Partnership', 'Subscription', 'Other'];

export const RevenueSection = ({
  revenue,
  onAddEntry,
  onEditEntry,
  onDeleteEntry,
}) => (
  <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-8" data-testid="revenue-tracking-section">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-emerald-600" />
        <h2 className="text-lg font-semibold text-stone-900">Revenue Tracking</h2>
      </div>
      <Button onClick={onAddEntry} size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full px-4 gap-1.5" data-testid="add-revenue-btn">
        <DollarSign className="w-3.5 h-3.5" /> Add Entry
      </Button>
    </div>

    {/* Summary Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="total-revenue-card">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Total Revenue</p>
        <p className="text-2xl font-bold text-emerald-800 mt-1">${(revenue.summary.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
      </div>
      <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="total-entries-card">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Entries</p>
        <p className="text-2xl font-bold text-stone-800 mt-1">{revenue.summary.entry_count}</p>
      </div>
      {Object.entries(revenue.summary.by_source || {}).slice(0, 2).map(([src, amt]) => (
        <div key={src} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 truncate">{src}</p>
          <p className="text-2xl font-bold text-amber-800 mt-1">${amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
      ))}
    </div>

    {/* Revenue Trend Chart */}
    {revenue.summary.monthly_trend?.length > 0 && (
      <div className="mb-6" data-testid="revenue-trend-chart">
        <h3 className="text-sm font-semibold text-stone-700 mb-3">Monthly Revenue</h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={revenue.summary.monthly_trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#57534E' }} />
            <YAxis tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={v => `$${v}`} />
            <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
              <div className="bg-white border border-stone-200 rounded-xl px-4 py-2.5 shadow-lg">
                <p className="text-xs font-medium text-stone-500">{label}</p>
                <p className="text-sm font-semibold text-emerald-700">${payload[0]?.value?.toLocaleString()}</p>
              </div>
            ) : null} />
            <Bar dataKey="amount" name="Revenue" fill="#059669" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}

    {/* Revenue Entries Table */}
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="revenue-table">
        <thead>
          <tr className="border-b border-stone-200">
            <th className="text-left py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">Date</th>
            <th className="text-left py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">Source</th>
            <th className="text-left py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">Description</th>
            <th className="text-right py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">Amount</th>
            <th className="text-right py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">Actions</th>
          </tr>
        </thead>
        <tbody>
          {revenue.entries.length === 0 ? (
            <tr><td colSpan={5} className="py-8 text-center text-stone-400">No revenue entries yet. Click "Add Entry" to start tracking.</td></tr>
          ) : revenue.entries.map((entry) => (
            <tr key={entry.id} className="border-b border-stone-100 hover:bg-[#FDFDF9] transition-colors">
              <td className="py-3 px-3 text-stone-600">{entry.date}</td>
              <td className="py-3 px-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">{entry.source}</span>
              </td>
              <td className="py-3 px-3 text-stone-500 max-w-[300px] truncate">{entry.description || '-'}</td>
              <td className="py-3 px-3 text-right font-semibold text-emerald-700">${entry.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className="py-3 px-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => onEditEntry(entry)} className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-500 hover:text-stone-700 flex items-center justify-center transition-colors" data-testid={`edit-revenue-${entry.id}`}>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  </button>
                  <button onClick={() => onDeleteEntry(entry.id)} className="w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 flex items-center justify-center transition-colors" data-testid={`delete-revenue-${entry.id}`}>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const RevenueDialog = ({
  open,
  onOpenChange,
  editingRevenue,
  revenueForm,
  setRevenueForm,
  onSave,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-stone-900">
          <DollarSign className="w-5 h-5 text-emerald-600" /> {editingRevenue ? 'Edit Revenue Entry' : 'Add Revenue Entry'}
        </DialogTitle>
        <DialogDescription>{editingRevenue ? 'Update the revenue details below.' : 'Record a new revenue entry.'}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSave} className="space-y-4 mt-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Amount ($) *</label>
            <Input type="number" step="0.01" min="0" value={revenueForm.amount} onChange={(e) => setRevenueForm({ ...revenueForm, amount: e.target.value })} placeholder="0.00" required data-testid="revenue-amount-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Date *</label>
            <Input type="date" value={revenueForm.date} onChange={(e) => setRevenueForm({ ...revenueForm, date: e.target.value })} required data-testid="revenue-date-input" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Source *</label>
          <select value={revenueForm.source} onChange={(e) => setRevenueForm({ ...revenueForm, source: e.target.value })} required className="w-full h-10 px-3 rounded-md border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="revenue-source-select">
            <option value="">Select source...</option>
            {REVENUE_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Description</label>
          <Input value={revenueForm.description} onChange={(e) => setRevenueForm({ ...revenueForm, description: e.target.value })} placeholder="Optional details..." data-testid="revenue-description-input" />
        </div>
        <div className="flex gap-3 justify-end pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full px-6" data-testid="revenue-save-btn">
            {editingRevenue ? 'Update' : 'Add Entry'}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
